import React, { FC, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { setHeader, setRoutes } from 'src/core/ui/shell';
import { AppConfig, container } from 'src/inversify.config';
import { RoleSummary, RoleSummaryStore } from 'src/stores/roles-store';
import { TableModel, TableView } from 'src/core/ui/collections/table';
import { Card, App, Row, Tag } from 'antd';
import { Query } from 'src/core/stores/data-store';
import { formatMessage } from 'src/core/services/http.service';
import { ENV } from 'src/';
import { emptyTableModel, getRoleDisplayName } from 'src/common/utils';
import { useNavigate } from 'react-router-dom';
import { AppConfiguration } from 'src/core/services/configuration.service';
import { roleIsLocked } from 'src/stores/default-roles-store';
import { AutocompleteTextFilter } from 'src/common/components/table-filters/autocomplete-text-filter';
import { OptionsFilter } from 'src/common/components/table-filters/options-filter';
import { Selector, SelectorValue } from 'src/common/types';

const RolesListPage: FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const config: AppConfiguration = useContext(AppConfig);

  const [query, setQuery] = useState({
    searchQuery: '',
    orderBy: [{ field: 'name', direction: 'Ascending', useProfile: false }],
    skip: 0,
    take: 10,
  } as Query);

  const rolesStore = container.get(RoleSummaryStore);
  const rolesState = rolesStore.state;
  const { modal } = App.useApp();

  setHeader(t('Roles'));
  setRoutes([
    { path: '', breadcrumbName: t('Home') },
    { path: 'roles', breadcrumbName: t('Roles') },
  ]);

  useEffect(
    () => {
      rolesStore.load(query);
    },
    [] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const onDelete = async (item: RoleSummary) => {
    modal.confirm({
      title: <h3>{`${t('Confirmation')}`}</h3>,
      icon: null,
      onOk: async () => {
        await rolesStore.delete(item.id);
        await rolesStore.load(query);
      },
      content: item.addToScopeTypeCreator == null ? t('RoleDelete') : t('RoleIsBeingAssignedToDefaultCreatorsDelete'),
      okText: t('Delete'),
      okButtonProps: { style: { width: 120 } },
      cancelText: t('Cancel'),
      cancelButtonProps: { type: 'default', style: { width: 120 } },
    });
  };

  const tableModel = {
    query: query,
    columns: [
      {
        sortable: true,
        searchable: true,
        field: 'name',
        title: t('Name'),
        applyPartialFilter: true,
        filter: <AutocompleteTextFilter baseUrl={rolesStore.baseUrl} fieldName={'Name'} fieldValue={'name'} />,
        fiterField: 'Name',
        renderer: (data) => <span>{getRoleDisplayName(data.name)}</span>,
      },
      {
        field: 'organization',
        useProfile: true,
        sortField: 'organization',
        sortable: true,
        searchable: true,
        title: t('Organization'),
        applyPartialFilter: true,
        filter: <AutocompleteTextFilter baseUrl={rolesStore.baseUrl} fieldName={'Organization/DisplayName'} fieldValue={'organization'} />,
        fiterField: 'Organization/DisplayName',
        renderer: (data: RoleSummary) => (
          <span>
            <Tag>{data.organization}</Tag>
          </span>
        ),
      },
      {
        sortable: true,
        field: 'selector',
        filter: <OptionsFilter options={Object.entries(Selector).map(([key, value]) => ({ label: t(key), value: value }))} />,
        title: t('Selector'),
        renderer: (data) => (
          <span>
            <Tag>{t(data.selector)}</Tag>
          </span>
        ),
      },
      {
        sortable: true,
        filter: (
          <OptionsFilter
            options={Object.entries(Selector)
              .filter(([_, value]) => SelectorValue[value] <= SelectorValue[Selector.Organization])
              .map(([key, value]) => ({ label: t(key), value: value }))}
          />
        ),
        field: 'addToScopeTypeCreator',
        title: t('AssignToScopeCreator'),
        renderer: (data) =>
          data.addToScopeTypeCreator && (
            <span>
              <Tag>{t(data.addToScopeTypeCreator)}</Tag>
            </span>
          ),
      },
      {
        renderer: (data: RoleSummary) =>
          !roleIsLocked(data.name) && (
            <>
              <span className="clickable-span strong" onClick={() => navigate(`update/${data.id}`, { state: data })}>
                {t('Edit')}
              </span>
              <span style={{ color: '#E1E1E1' }}>{' | '}</span>{' '}
              <span className="clickable-span strong" onClick={() => onDelete(data)}>
                {t('Delete')}
              </span>
            </>
          ),
      },
    ],
    data: rolesState.value,
  } as TableModel<RoleSummary>;

  return (
    <Row align="top" justify="space-between">
      <Card className="card-table" style={{ width: '100%' }}>
        <TableView
          rowKey="id"
          canCreateNew
          onNewItem={() => navigate(`new`)}
          newItemName={t('CreateRole')}
          canDelete={false}
          canEdit={false}
          canSort
          onQueryChanged={(query: Query) => {
            setQuery(query);
            rolesStore.load(query);
          }}
          onRefresh={() => rolesStore.load(query)}
          model={rolesState.errorMessage.value ? emptyTableModel : tableModel}
          error={rolesState.errorMessage.value && (config.environment === ENV.DEV ? formatMessage(rolesState.value) : t('RoleListTableError'))}
        />
      </Card>
    </Row>
  );
};

export default RolesListPage;
